/**
 * Builds EZLynx-format XML files from Branch offer data for EZLynx import.
 * Two exported functions: buildAutoXml and buildHomeXml.
 *
 * Format: EZLynx proprietary V200 schema (NOT ACORD).
 *   Auto root: <EZAUTO xmlns="http://www.ezlynx.com/XMLSchema/Auto/V200">
 *   Home root: <EZHOME xmlns="http://www.ezlynx.com/XMLSchema/Home/V200">
 *
 * Element ordering and shape inside typed sections (Coverages, RatingInfo,
 * ReplacementCost, RatingCredits, Endorsements) follows the V200 XSD strictly:
 * unknown children inside those sections cause the import to fail with
 * "Error in deserializing to V200 EzHome/EzAuto".
 *
 * Branch coverage data and other non-schema values are carried in a
 * trailing <ExtendedInfo name="BRANCH_DATA"> block as <valuepair> entries.
 * The V200 schema accepts arbitrary <ExtendedInfo> blocks (this is the same
 * mechanism EZLynx uses for carrier-specific data on its own exports).
 *
 * GitHub issue: #29109
 */

import { policyTypes } from '@ourbranch/policy-types';

import { EZLYNX_PRIOR_CARRIERS } from './ezlynx-prior-carriers';

function escapeXml(str) {
  if (str == null) {
    return '';
  }
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function el(tag, value, attrs = '') {
  const attrStr = attrs ? ` ${attrs}` : '';
  if (value == null || value === '') {
    return `<${tag}${attrStr} />`;
  }
  return `<${tag}${attrStr}>${escapeXml(value)}</${tag}>`;
}

function elOmitIfEmpty(tag, value) {
  if (value == null || value === '') {
    return '';
  }
  return `<${tag}>${escapeXml(value)}</${tag}>`;
}

function wrap(tag, children, attrs = '') {
  const filtered = (children || []).filter(Boolean);
  const attrStr = attrs ? ` ${attrs}` : '';
  if (filtered.length === 0) {
    return `<${tag}${attrStr} />`;
  }
  return `<${tag}${attrStr}>\n${filtered.join('\n')}\n</${tag}>`;
}

function formatDate(dateStr) {
  if (!dateStr) {
    return '';
  }
  return String(dateStr).slice(0, 10);
}

function splitAddress(address, address2) {
  if (!address) {
    return { streetNumber: '', streetName: '', unitNumber: address2 || '' };
  }
  const trimmed = String(address).trim();
  const match = trimmed.match(/^(\d+\S*)\s+(.+)$/);
  if (match) {
    return { streetNumber: match[1], streetName: match[2], unitNumber: address2 || '' };
  }
  return { streetNumber: '', streetName: trimmed, unitNumber: address2 || '' };
}

function splitZip(zip) {
  if (!zip) {
    return { zip5: '', zip4: '' };
  }
  const digitsOnly = String(zip).replace(/\D/g, '');
  if (digitsOnly.length >= 9) {
    return { zip5: digitsOnly.slice(0, 5), zip4: digitsOnly.slice(5, 9) };
  }
  return { zip5: digitsOnly.slice(0, 5), zip4: '' };
}

function deriveHomePolicyType(selectedOption) {
  if (!selectedOption) {
    return 'HO3';
  }
  if (selectedOption === policyTypes.Renters || selectedOption === policyTypes.ARBundle) {
    return 'HO4';
  }
  if (selectedOption === policyTypes.Condo || selectedOption === policyTypes.CABundle) {
    return 'HO6';
  }
  return 'HO3';
}

function isBundle(selectedOption) {
  if (!selectedOption) {
    return false;
  }
  return [policyTypes.HABundle, policyTypes.ARBundle, policyTypes.CABundle, policyTypes.FABundle].includes(
    selectedOption
  );
}

// Branch stores gender / maritalStatus as single-letter codes (M, F, S, D, W).
// EZLynx V200 enums expect the full word ("Male", "Female", "Married", etc.).
// Normalizers accept either form for forward compatibility.
function normalizeGender(value) {
  if (!value) {
    return '';
  }
  const v = String(value).trim().toUpperCase();
  if (v === 'M' || v === 'MALE') {
    return 'Male';
  }
  if (v === 'F' || v === 'FEMALE') {
    return 'Female';
  }
  return value;
}

function normalizeMaritalStatus(value) {
  if (!value) {
    return '';
  }
  const v = String(value).trim().toUpperCase();
  if (v === 'M' || v === 'MARRIED') {
    return 'Married';
  }
  if (v === 'S' || v === 'SINGLE') {
    return 'Single';
  }
  if (v === 'D' || v === 'DIVORCED') {
    return 'Divorced';
  }
  if (v === 'W' || v === 'WIDOWED') {
    return 'Widowed';
  }
  if (v === 'P' || v === 'SEPARATED') {
    return 'Separated';
  }
  return value;
}

function derivePassiveRestraints(car) {
  if (car?.doubleAirbagStatus === 'O' || car?.doubleAirbagStatus === 'S') {
    return 'Airbag Both Sides';
  }
  if (car?.singleAirbagStatus === 'O' || car?.singleAirbagStatus === 'S') {
    return 'Driver Side';
  }
  if (car?.doubleAirbagStatus === 'N' && car?.singleAirbagStatus === 'N') {
    return 'None';
  }
  return '';
}

// Branch tracks an explicit per-driver spousal relationship via
// `relationshipCode`. 'SP' means Spouse — that's the same signal the rater
// uses to pick a CoApplicant (see packages/rater/src/retrieve-data-auto.js).
// Using `maritalStatus === 'Married'` would false-positive on adult
// children / parents who are also married but not to the insured.
function findSpouseDriver(drivers) {
  if (!Array.isArray(drivers)) {
    return null;
  }
  return drivers.find((d) => !d.isPrimary && d.relationshipCode === 'SP') || null;
}

function deriveDriverRelation(driver, isPrimary) {
  if (isPrimary) {
    return 'Insured';
  }
  if (driver.relationshipCode === 'SP') {
    return 'Spouse';
  }
  // Branch doesn't store granular non-spouse relationships consistently, so
  // default to 'Child' for all other dependents.
  return 'Child';
}

function buildAddressBlock({ address, address2, city, state, county, zip, phone, format, applicantType }) {
  const { streetNumber, streetName, unitNumber } = splitAddress(address, address2);
  const { zip5, zip4 } = splitZip(zip);
  const isPrimary = applicantType === 'Applicant';
  return wrap('Address', [
    el('AddressCode', 'StreetAddress'),
    wrap('Addr1', [el('StreetName', streetName), el('StreetNumber', streetNumber), el('UnitNumber', unitNumber)]),
    el('Addr2', ''),
    el('City', city),
    el('StateCode', state),
    // County is passed through as-is. Branch's data pulls return upper-case
    // ("PULASKI"), and V200 accepts that. An earlier title-case attempt was
    // unnecessary; the actual home import bug was *Update casing, not county.
    elOmitIfEmpty('County', county),
    el('Zip5', zip5),
    elOmitIfEmpty('Zip4', zip4),
    phone ? wrap('Phone', [el('PhoneType', 'Mobile'), el('PhoneNumber', String(phone).replace(/\D/g, ''))]) : '',
    // Neither V200 auto nor home schema include <Email> inside <Address>.
    // Email's already on the customer record in EZLynx via other channels.
    isPrimary ? el('BridgeEmailToCarriers', 'No') : '',
    isPrimary && phone ? el('PreferredContactMethod', 'Phone (Mobile)') : '',
    format === 'home' ? el('YearsAtAddress', 'More than 10') : '',
    el('Validation', 'Valid')
  ]);
}

// Industry/Occupation values must come from the EZLynx V200 enum. Branch
// doesn't track these fields, so we use a safe default that's confirmed valid
// in multiple V200 references (Kurt_Muldrew-clean_Auto.xml and _Home.xml).
// "Unemployed/Retired/Student" is NOT in V200's enum and was failing imports.
const DEFAULT_INDUSTRY = 'Retired';
const DEFAULT_OCCUPATION = 'Retired';

// Branch stores most home fields as single-letter lookup codes. EZLynx V200 home
// schema expects display strings, but the strings don't always match the
// values Branch's @ourbranch/lookups package yields. These maps translate either
// the Branch code or the Branch display value into the V200-accepted string.
// Verified against /Users/ivanherndon/Downloads/Kurt_Muldrew-clean_Home.xml.
const BRANCH_TO_V200_DWELLING = {
  1: 'One Family',
  3: 'Town Home',
  4: 'Other',
  'Single Home': 'One Family',
  'Town Home': 'Town Home',
  Other: 'Other'
};
const BRANCH_TO_V200_CONSTRUCTION = {
  B: 'Brick',
  F: 'Frame',
  Brick: 'Brick',
  Frame: 'Frame'
};
// V200 home Roof enum is UPPERCASE — same casing convention as the *Update
// enum. Branch's roofType lookup doesn't track tile sub-types (clay /
// concrete / spanish), so "T" maps to generic "TILE" by default. AZ's V200
// importer rejects bare "TILE" and requires a sub-type — see
// translateV200Roof below for the state-specific override.
const BRANCH_TO_V200_ROOF = {
  W: 'WOOD SHINGLES',
  S: 'SLATE',
  T: 'TILE',
  M: 'METAL',
  C: 'ASPHALT SHINGLES',
  O: 'OTHER',
  Wood: 'WOOD SHINGLES',
  Slate: 'SLATE',
  Tile: 'TILE',
  Metal: 'METAL',
  Composition: 'ASPHALT SHINGLES',
  Other: 'OTHER'
};
// Only "Basement - Partially Finished" is verified valid in a working V200
// reference (Kurt_Muldrew-clean_Home.xml). The "Basement - Unfinished" /
// "Basement - Fully Finished" / "Slab" / "Crawlspace" labels are common
// insurance enums but EZLynx's V200 may use different strings. Map every
// Branch basementType to the verified value as a safe default until we have
// references for the other states. Lossy, but the import succeeds.
const BRANCH_TO_V200_FOUNDATION = {
  S: 'Basement - Partially Finished',
  R: 'Basement - Partially Finished',
  U: 'Basement - Partially Finished',
  PF: 'Basement - Partially Finished',
  FF: 'Basement - Partially Finished'
};
const BRANCH_TO_V200_ROOF_DESIGN = {
  G: 'Gable',
  H: 'Hip',
  F: 'Flat',
  O: 'Other'
};
const BRANCH_TO_V200_DWELLING_USE = {
  P: 'Primary',
  PS: 'Primary',
  S: 'Secondary'
};
// Branch's exteriorWallType (publicLookups.json) → V200 ExteriorWalls enum.
// The Branch "S" code ("Siding - Other (non-wood)") has no clean V200 equivalent
// (V200 has no generic "Other"); we deliberately omit it from this map so
// `translateV200` returns '' and `elOmitIfEmpty` drops the element rather than
// emitting a guessed value that might fail the V200 enum.
const BRANCH_TO_V200_EXTERIOR_WALLS = {
  B: 'Brick Veneer',
  AL: 'Siding, Aluminum',
  ASB: 'Siding, Hardboard',
  T111: 'Siding, T-111',
  VL: 'Siding, Vinyl',
  T: 'Stucco',
  W: 'Siding, Wood'
};

function translateV200(map, value) {
  if (value == null || value === '') {
    return '';
  }
  return map[value] || '';
}

// AZ's V200 importer rejects the generic "TILE" Roof value — it requires a
// sub-type. Concrete tile is verified to import cleanly for AZ homes; clay
// and spanish are documented V200 alternates but unused today because
// Branch's roofType code doesn't distinguish sub-types.
export function translateV200Roof(roofType, state) {
  const base = translateV200(BRANCH_TO_V200_ROOF, roofType);
  if (base === 'TILE' && state === 'AZ') {
    return 'TILE(CONCRETE)';
  }
  return base;
}

function buildAutoPersonalInfo({ person, applicantType }) {
  return wrap('PersonalInfo', [
    wrap('Name', [
      el('FirstName', person.firstName || person.fname),
      el('MiddleName', person.middleName || ''),
      el('LastName', person.lastName || person.lname)
    ]),
    elOmitIfEmpty('DOB', formatDate(person.dateOfBirth || person.birthDate)),
    elOmitIfEmpty('Gender', normalizeGender(person.gender)),
    elOmitIfEmpty('MaritalStatus', normalizeMaritalStatus(person.maritalStatus)),
    el('Industry', DEFAULT_INDUSTRY),
    el('Occupation', DEFAULT_OCCUPATION),
    el('Relation', applicantType === 'Applicant' ? 'Insured' : 'Spouse')
  ]);
}

function buildHomePersonalInfo({ person, applicantType }) {
  return wrap('PersonalInfo', [
    wrap('Name', [
      el('FirstName', person.firstName || person.fname),
      el('MiddleName', person.middleName || ''),
      el('LastName', person.lastName || person.lname)
    ]),
    elOmitIfEmpty('DOB', formatDate(person.dateOfBirth || person.birthDate)),
    elOmitIfEmpty('Gender', normalizeGender(person.gender)),
    elOmitIfEmpty('MaritalStatus', normalizeMaritalStatus(person.maritalStatus)),
    el('Relation', applicantType === 'Applicant' ? 'Insured' : 'Spouse'),
    el('Industry', DEFAULT_INDUSTRY),
    el('Occupation', DEFAULT_OCCUPATION)
  ]);
}

function buildApplicantBlock({ applicantType, person, addr, phone, format }) {
  const personalInfo =
    format === 'home'
      ? buildHomePersonalInfo({ person, applicantType })
      : buildAutoPersonalInfo({ person, applicantType });

  return wrap('Applicant', [
    el('ApplicantType', applicantType),
    personalInfo,
    buildAddressBlock({
      address: addr.address,
      address2: addr.address2,
      city: addr.city,
      state: addr.state,
      county: addr.county,
      zip: addr.zip,
      phone,
      format,
      applicantType
    })
  ]);
}

// --- AUTO -----------------------------------------------------------------

function buildAutoDriverBlock(driver, index, principalVehicleIndex) {
  const driverId = String(index + 1);
  const isPrimary = !!driver.isPrimary;
  const relation = deriveDriverRelation(driver, isPrimary);
  return wrap(
    'Driver',
    [
      wrap('Name', [el('FirstName', driver.firstName), el('LastName', driver.lastName)]),
      elOmitIfEmpty('Gender', normalizeGender(driver.gender)),
      elOmitIfEmpty('DOB', formatDate(driver.dateOfBirth || driver.birthDate)),
      el('DLNumber', driver.driversLicenseNumber || ''),
      elOmitIfEmpty('DLState', driver.driversLicenseState),
      el('DLStatus', 'Valid'),
      elOmitIfEmpty('AgeLicensed', driver.ageFirstLicensed),
      elOmitIfEmpty('MaritalStatus', normalizeMaritalStatus(driver.maritalStatus)),
      el('Relation', relation),
      el('Industry', DEFAULT_INDUSTRY),
      el('Occupation', DEFAULT_OCCUPATION),
      el('PrincipalVehicle', principalVehicleIndex > 0 ? String(principalVehicleIndex) : '0'),
      el('Rated', driver.excludeDriver ? 'Excluded' : 'Rated'),
      el('SR22', driver.needSR22 ? 'Yes' : 'No'),
      el('FR44', 'No'),
      el('LicenseRevokedSuspended', driver.lapsedLicense ? 'Yes' : 'No')
    ],
    `id="${driverId}"`
  );
}

function resolveAssignedVehicleIndex(driver, cars) {
  if (!driver?.assignedCar || !Array.isArray(cars)) {
    return 0;
  }
  const idx = cars.findIndex((car) => car.VIN === driver.assignedCar);
  return idx >= 0 ? idx + 1 : 0;
}

function buildVehicleBlock(car, index) {
  const vehId = String(index + 1);
  return wrap(
    'Vehicle',
    [
      el('UseVinLookup', 'No'),
      elOmitIfEmpty('Year', car.year),
      elOmitIfEmpty('Vin', car.VIN),
      elOmitIfEmpty('Make', car.make ? String(car.make).toUpperCase() : ''),
      elOmitIfEmpty('Model', car.model ? String(car.model).toUpperCase() : ''),
      // Reference always populates Sub-Model; empty self-close may fail schema
      // validation. Branch's bodyType is null for many cars — default to the
      // Model value as a reasonable fallback.
      el(
        'Sub-Model',
        car.bodyType ? String(car.bodyType).toUpperCase() : car.model ? String(car.model).toUpperCase() : 'OTHER'
      ),
      el('PassiveRestraints', derivePassiveRestraints(car) || 'None'),
      el('AntiLockBrake', 'Yes'),
      el('DaytimeRunningLights', 'Yes'),
      el('Telematics', 'No'),
      el('TransportationNetworkCompany', car.rideSharing ? 'Yes' : 'No'),
      elOmitIfEmpty('VehiclePurchaseDate', formatDate(car.purchaseDate))
    ],
    `id="${vehId}"`
  );
}

function buildVehicleUseBlock(car, principalDriverIndex) {
  const annualMiles = car.annualMilesDriven ?? car.verifiedAnnualMilesDriven ?? 0;
  return wrap('VehicleUse', [
    el('OneWayMiles', '0'),
    el('AnnualMiles', String(annualMiles)),
    el('Odometer', '0'),
    el('AdditionalModificationValue', '0'),
    el('PrincipalOperator', principalDriverIndex > 0 ? String(principalDriverIndex) : '0'),
    el('CostNew', car.msrp != null ? String(Math.round(car.msrp)) : '0'),
    el('UsedForDelivery', 'No'),
    el('PriorDamagePresent', car.brandedTitle ? 'Yes' : 'No')
  ]);
}

function buildVehicleAssignments(cars, drivers) {
  return wrap(
    'VehicleAssignments',
    cars.map((car, vehIdx) => {
      const principalDriverIndex = drivers.findIndex((d) => d.assignedCar && d.assignedCar === car.VIN);
      const driverAssignments = drivers.map((_d, drvIdx) =>
        el('DriverAssignment', drvIdx === principalDriverIndex ? '100' : '0', `id="${drvIdx + 1}"`)
      );
      return wrap('VehicleAssignment', driverAssignments, `id="${vehIdx + 1}"`);
    })
  );
}

// Splits Branch's tri-part BIPD limit (e.g. "100/300/100") into the asymmetric
// formats V200 expects: BI in thousand-shorthand ("100/300") and PD in full
// dollars ("100000"). Verified against Kurt_Muldrew-clean_Auto.xml which has
// <BI>100/300</BI> and <PD>100000</PD>.
function splitBIPD(bipd) {
  if (!bipd) {
    return { bi: '', pd: '' };
  }
  const parts = String(bipd).split('/');
  const toFullDollars = (thousands) => {
    const n = parseInt(thousands, 10);
    return Number.isFinite(n) ? String(n * 1000) : '';
  };
  if (parts.length >= 3) {
    return { bi: `${parts[0]}/${parts[1]}`, pd: toFullDollars(parts[2]) };
  }
  if (parts.length === 2) {
    return { bi: `${parts[0]}/${parts[1]}`, pd: '' };
  }
  return { bi: parts[0] || '', pd: '' };
}

// V200 auto coverage uses SHORT element names (BI/PD/MP/UM/UIM). Long names
// like <BodilyInjury> are accepted structurally (xs:any) but EZLynx's parser
// ignores them — values never reach the UI. Verified against
// /Users/ivanherndon/Downloads/Kurt_Muldrew-clean_Auto.xml.
function normalizeMpValue(value) {
  if (!value) {
    return 'None';
  }
  // Branch stores "NONE" / "0" as the no-coverage code, but V200's MP enum
  // uses the literal mixed-case "None".
  const s = String(value).trim();
  if (s === '' || s === '0' || s.toUpperCase() === 'NONE') {
    return 'None';
  }
  return s;
}

// V200 UM/UIM enum accepts limit pairs ("25/50", "100/300") OR sentinels for
// no-real-coverage shapes. Branch stores "0/0" or "NONE" when the insured carries
// only the state-mandated minimum; emitting either of those literally fails the
// V200 enum, so map them to "State Minimum" (the V200 sentinel).
function normalizeUMUIMValue(value) {
  if (!value) {
    return '';
  }
  const s = String(value).trim();
  if (s === '' || s === '0/0' || s.toUpperCase() === 'NONE') {
    return 'State Minimum';
  }
  return s;
}

function buildGeneralCoverage(autoCoverage, optionType) {
  const cov = autoCoverage || {};
  const { bi, pd } = splitBIPD(cov.policyLimitBIPD);
  return wrap('GeneralCoverage', [
    elOmitIfEmpty('BI', bi),
    elOmitIfEmpty('PD', pd),
    el('MP', normalizeMpValue(cov.policyLimitMedicalPayments)),
    elOmitIfEmpty('UM', normalizeUMUIMValue(cov.policyLimitUMBI)),
    elOmitIfEmpty('UIM', normalizeUMUIMValue(cov.policyLimitUIMBI)),
    el('Multipolicy', isBundle(optionType) ? 'Yes' : 'No')
  ]);
}

// V200 rental coverage is per-day / 30-day-max in slash format ("30/900",
// "40/1200"...). Branch stores just the per-day rate (e.g. "40"). Convert
// Branch's bare code to the V200 enum value, or "No Coverage" for NONE/empty.
function normalizeRentalDeductible(limitRental) {
  if (!limitRental) {
    return 'No Coverage';
  }
  const s = String(limitRental).trim();
  if (s === '' || s === '0' || s.toUpperCase() === 'NONE') {
    return 'No Coverage';
  }
  const perDay = parseInt(s, 10);
  if (!Number.isFinite(perDay)) {
    return 'No Coverage';
  }
  return `${perDay}/${perDay * 30}`;
}

// V200 deductible enum accepts numeric strings ("250", "500", "1000") OR
// "No Coverage" for liability-only vehicles. Branch stores "NONE" / "0" /
// empty when comp or collision isn't carried; emitting any of those literally
// fails the V200 enum, so map them to "No Coverage".
//
// Branch's deductibleComprehensive enum also includes composite values like
// "1000/0GL" ($1,000 comp with $0 glass deductible) and "1000/50GL" — Branch
// encodes the comp deductible and a separate glass deductible in one field.
// V200 has no concept of a separate glass deductible (FullGlass is a different
// boolean field), so flatten by taking the leading numeric value. Verified
// against /Users/ivanherndon/Downloads/EasyKey/not working/easykey-auto-…
// where "1000/0GL" broke an AZ auto import; "1000" alone imports cleanly.
// deductibleCollision never contains slashes, so this is a no-op for it.
export function normalizeDeductible(value) {
  if (value == null || value === '') {
    return 'No Coverage';
  }
  const s = String(value).trim();
  if (s === '' || s === '0' || s.toUpperCase() === 'NONE') {
    return 'No Coverage';
  }
  const slashIdx = s.indexOf('/');
  if (slashIdx > 0) {
    return s.slice(0, slashIdx);
  }
  return s;
}

function buildVehicleCoverage(car, index) {
  const id = String(index + 1);
  return wrap(
    'VehicleCoverage',
    [
      el('OtherCollisionDeductible', normalizeDeductible(car.deductibleComprehensive)),
      el('CollisionDeductible', normalizeDeductible(car.deductibleCollision)),
      el('FullGlass', car.windowGlass ? 'Yes' : 'No'),
      el('TowingDeductible', 'No Coverage'),
      el('RentalDeductible', normalizeRentalDeductible(car.limitRental)),
      el('LiabilityNotRequired', 'No'),
      el('LoanLeaseCoverage', car.coverageLoan ? 'Yes' : 'No')
    ],
    `id="${id}"`
  );
}

// State-specific coverage children vary per state (e.g. PA has PA-StackedUM,
// PA-TortLimitation; other states have entirely different fields). When we
// don't know the state's required children, emit an empty <{state}-Coverages />.
function buildStateSpecificCoverage(state) {
  if (!state) {
    return '<StateSpecificCoverage />';
  }
  return wrap('StateSpecificCoverage', [`<${state}-Coverages />`]);
}

function buildAutoCoverages(cars, autoCoverage, state, optionType) {
  return wrap('Coverages', [
    buildGeneralCoverage(autoCoverage, optionType),
    ...cars.map((car, i) => buildVehicleCoverage(car, i)),
    buildStateSpecificCoverage(state)
  ]);
}

// Computes elapsed { years, months } from `startDate` to today. Returns zeros
// (not null) when startDate is missing/invalid so the caller can still emit
// the schema-required <Years>/<Months> children.
function elapsedYearsMonths(startDate) {
  if (!startDate) {
    return { years: 0, months: 0 };
  }
  const start = new Date(startDate);
  if (Number.isNaN(start.getTime())) {
    return { years: 0, months: 0 };
  }
  const now = new Date();
  let totalMonths = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  if (now.getDate() < start.getDate()) {
    totalMonths -= 1;
  }
  if (totalMonths < 0) {
    totalMonths = 0;
  }
  return { years: Math.floor(totalMonths / 12), months: totalMonths % 12 };
}

// Case-insensitive lookup over the V200 PriorCarrier enum, built once at
// module load. 297 entries, immutable input — no reason for the singleton to
// be mutable, and eager construction keeps tests deterministic.
const PRIOR_CARRIER_LOOKUP = new Map(EZLYNX_PRIOR_CARRIERS.map((c) => [c.toUpperCase(), c]));

// Enum entries sorted by length descending for the substring scan. Longest
// matches win, so a Branch string like "DIRECT GENERAL AUTO" prefers
// "Direct General" over the shorter "Direct".
const PRIOR_CARRIER_BY_LENGTH_DESC = [...EZLYNX_PRIOR_CARRIERS].sort((a, b) => b.length - a.length);

function escapeRegex(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Maps Branch's free-text carrier strings ("PROGRESSIVE GROUPS", "STATE FARM
// MUTUAL", "GEICO INDEMNITY CO") into V200's PriorCarrier enum. Branch stores
// whatever the data pull returned, so we have to normalize before emit or the
// V200 importer rejects the value as out-of-enum.
//
// Resolution order:
//   1. Exact case-insensitive match — most precise, do this first.
//   2. Strip noise suffixes (GROUP[S], INSURANCE/INS, INC, CO, COMPANY, MUTUAL,
//      CORP) and retry exact match. Handles "PROGRESSIVE GROUPS" → "Progressive"
//      and "STATE FARM MUTUAL" → "State Farm".
//   3. Longest whole-word substring scan against the original input. The
//      earlier prefix-based approach mis-matched "THE HARTFORD" → "The General"
//      and "DIRECT AUTO" → "Direct General" because both pairs share a leading
//      word and Map insertion order picked the wrong one. Word-bounded
//      substring matching is order-independent and prefers specific matches.
//   4. Fallback to "Other Standard" (explicit V200 catch-all) so we still emit
//      prior-carrier info instead of dropping it silently.
export function normalizePriorCarrier(branchCarrier) {
  if (!branchCarrier) {
    return '';
  }
  const upper = String(branchCarrier).trim().toUpperCase();
  if (!upper) {
    return '';
  }

  const direct = PRIOR_CARRIER_LOOKUP.get(upper);
  if (direct) {
    return direct;
  }

  const stripped = upper
    .replace(/\s+(GROUPS?|INSURANCE|INS|INC|CO|COMPANY|MUTUAL|CORP)\b/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  const stripMatch = PRIOR_CARRIER_LOOKUP.get(stripped);
  if (stripMatch) {
    return stripMatch;
  }

  // Step 3 runs against the ORIGINAL upper input, not the stripped version,
  // so a Branch string like "STANDARD MUTUAL" can still match "Standard
  // Mutual" instead of being stripped to "STANDARD" and falling to "Other
  // Standard". Longest-first iteration picks the most specific enum entry.
  // The earlier prefix-based approach (incl. the AMBIGUOUS_LEAD_WORDS guard
  // for {THE, A, AN}) only suppressed one symptom of the underlying issue —
  // Map insertion order determining matches — and didn't fix "DIRECT AUTO"
  // (which falsely matched "Direct General").
  for (const candidate of PRIOR_CARRIER_BY_LENGTH_DESC) {
    const pattern = new RegExp(`(^|\\s)${escapeRegex(candidate.toUpperCase())}(\\s|$)`);
    if (pattern.test(upper)) {
      return candidate;
    }
  }

  return 'Other Standard';
}

// V200 auto <PriorPolicyInfo>'s <Years> element is a closed string enum
// capped at "More than 15" (confirmed via EZLynx UI dropdown). Valid values:
//   "0", "1", ..., "15", "More than 15"
// Branch's `elapsedYearsMonths` computes raw integer years from the customer's
// carrier start date — for long-tenured customers that exceeds the cap and
// EZLynx rejects the import (see bd branch-14j / MELISSA_WALHOVD's 29y9m).
// When clamping to the sentinel, also force Months to "0" since months are
// meaningless past the cap (verified by hand-edit upload).
export function formatPriorCarrierDuration({ years, months } = {}) {
  const y = Math.floor(Number(years) || 0);
  const m = Math.min(11, Math.max(0, Math.floor(Number(months) || 0)));
  if (y > 15) {
    return { years: 'More than 15', months: '0' };
  }
  return { years: String(y), months: String(m) };
}

function buildAutoPriorPolicyInfo(global) {
  const g = global || {};
  const priorCarrier = g.currentlyAutoInsured ? normalizePriorCarrier(g.currentAutoCarrier) : '';
  const expiration = formatDate(g.mostRecentAutoInsuranceCoverageEndDate || g.currentAutoCarrierEndDate);
  const withCarrier = formatPriorCarrierDuration(elapsedYearsMonths(g.currentAutoCarrierStartDate));
  const continuous = formatPriorCarrierDuration(
    elapsedYearsMonths(g.continuousAutoCoverageStartDate || g.currentAutoCarrierStartDate)
  );
  return wrap('PriorPolicyInfo', [
    elOmitIfEmpty('PriorCarrier', priorCarrier),
    elOmitIfEmpty('Expiration', expiration),
    wrap('YearsWithPriorCarrier', [el('Years', withCarrier.years), el('Months', withCarrier.months)]),
    wrap('YearsWithContinuousCoverage', [el('Years', continuous.years), el('Months', continuous.months)])
  ]);
}

// Branch's residenceInsuranceType lookup IDs map to EZLynx's V200 Ownership enum.
// EZLynx values verified against Kurt_Muldrew-clean_Auto.xml ("Home (owned)").
// Other values are best-effort matches against typical V200 enums; refine as we
// discover the exact accepted strings.
const BRANCH_TO_V200_OWNERSHIP = {
  O: 'Home (owned)',
  M: 'Mobile Home (owned)',
  C: 'Condo (owned)',
  R: 'Renting (Apt/House/Etc.)',
  T: 'None'
};

function resolveOwnership(code) {
  if (!code) {
    return '';
  }
  return BRANCH_TO_V200_OWNERSHIP[code] || '';
}

function valuePair(name, value) {
  if (value == null || value === '') {
    return wrap('valuepair', [el('value', '')], `name="${escapeXml(name)}"`);
  }
  return wrap('valuepair', [el('value', String(value))], `name="${escapeXml(name)}"`);
}

// biome-ignore lint/correctness/noUnusedVariables: kept for re-enable once a working ExtendedInfo name is found
function buildAutoExtendedInfo({ autoCoverage, cars }) {
  const cov = autoCoverage || {};
  const pairs = [
    valuePair('auto.policyLimitBIPD', cov.policyLimitBIPD),
    valuePair('auto.policyLimitUMBI', cov.policyLimitUMBI),
    valuePair('auto.policyLimitUIMBI', cov.policyLimitUIMBI),
    valuePair('auto.policyLimitUMPD', cov.policyLimitUMPD),
    valuePair('auto.policyLimitMedicalPayments', cov.policyLimitMedicalPayments),
    valuePair('auto.policyLimitPIP', cov.policyLimitPIP),
    valuePair('auto.policyLimitLPD', cov.policyLimitLPD),
    cov.petInjury != null ? valuePair('auto.petInjury', cov.petInjury ? 'Yes' : 'No') : ''
  ].filter(Boolean);

  cars.forEach((car, i) => {
    const id = i + 1;
    pairs.push(
      valuePair(`vehicle.${id}.deductibleCollision`, car.deductibleCollision),
      valuePair(`vehicle.${id}.deductibleComprehensive`, car.deductibleComprehensive),
      valuePair(`vehicle.${id}.limitUMPD`, car.limitUMPD),
      valuePair(`vehicle.${id}.limitRental`, car.limitRental),
      valuePair(`vehicle.${id}.limitACPE`, car.limitACPE),
      car.roadsideAssistance != null
        ? valuePair(`vehicle.${id}.roadsideAssistance`, car.roadsideAssistance ? 'Yes' : 'No')
        : '',
      car.windowGlass != null ? valuePair(`vehicle.${id}.windowGlass`, car.windowGlass ? 'Yes' : 'No') : '',
      car.coverageLoan != null ? valuePair(`vehicle.${id}.coverageLoan`, car.coverageLoan ? 'Yes' : 'No') : '',
      car.rideSharing != null ? valuePair(`vehicle.${id}.rideSharing`, car.rideSharing ? 'Yes' : 'No') : ''
    );
  });

  return wrap('ExtendedInfo', pairs.filter(Boolean), 'name="BRANCH_DATA"');
}

export function buildAutoXml({ offer, selectedOption }) {
  const quote = offer?.quote;
  if (!quote) {
    return '';
  }

  const addr = quote.correctedAddress || {};
  const cars = quote.cars || [];
  const drivers = quote.drivers || [];
  const option = selectedOption || {};
  const state = addr.state || '';
  const optionType = quote.selectedOption || option.type;

  const primaryDriver = drivers.find((d) => d.isPrimary) || drivers[0];
  const spouseDriver = findSpouseDriver(drivers);

  const applicantPerson = {
    firstName: quote.fname,
    lastName: quote.lname,
    middleName: '',
    dateOfBirth: primaryDriver?.dateOfBirth,
    gender: primaryDriver?.gender,
    maritalStatus: primaryDriver?.maritalStatus
  };

  const applicants = [
    buildApplicantBlock({
      applicantType: 'Applicant',
      person: applicantPerson,
      addr,
      phone: quote.phone,
      format: 'auto'
    })
  ];
  if (spouseDriver) {
    applicants.push(
      buildApplicantBlock({
        applicantType: 'CoApplicant',
        person: spouseDriver,
        addr,
        phone: quote.phone,
        format: 'auto'
      })
    );
  }

  const driverBlocks = drivers.map((d, i) => buildAutoDriverBlock(d, i, resolveAssignedVehicleIndex(d, cars)));

  const vehicleBlocks = cars.map(buildVehicleBlock);
  const vehicleUseBlocks = cars.map((car) => {
    const principalDriverIdx = drivers.findIndex((d) => d.assignedCar === car.VIN);
    return buildVehicleUseBlock(car, principalDriverIdx >= 0 ? principalDriverIdx + 1 : 0);
  });

  const ezAuto = wrap(
    'EZAUTO',
    [
      ...applicants,
      buildAutoPriorPolicyInfo(quote.global),
      wrap('PolicyInfo', [
        el('PolicyTerm', '6 Month'),
        el('Package', isBundle(optionType) ? 'Yes' : 'No'),
        elOmitIfEmpty('Effective', formatDate(quote.global?.autoEffectiveDate)),
        el('CreditCheckAuth', 'Yes')
      ]),
      wrap('ResidenceInfo', [
        wrap('CurrentAddress', [
          wrap('YearsAtCurrent', [el('Years', '0'), el('Months', '0')]),
          elOmitIfEmpty('Ownership', resolveOwnership(quote.auto?.residenceInsuranceType))
        ])
      ]),
      wrap('Drivers', driverBlocks),
      wrap('Vehicles', vehicleBlocks),
      wrap('VehiclesUse', vehicleUseBlocks),
      buildAutoCoverages(cars, quote.autoCoverage, state, optionType),
      buildVehicleAssignments(cars, drivers),
      wrap('GeneralInfo', [el('RatingStateCode', state)])
      // Diagnostic step: BRANCH_DATA ExtendedInfo block omitted because it is
      // not in the EZLynx reference XML files. Re-enable after import works.
      // buildAutoExtendedInfo({ autoCoverage: quote.autoCoverage, cars })
    ],
    'xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:xsd="http://www.w3.org/2001/XMLSchema" xmlns="http://www.ezlynx.com/XMLSchema/Auto/V200"'
  );

  return `<?xml version="1.0" encoding="utf-8"?>\n${ezAuto}\n`;
}

// --- HOME -----------------------------------------------------------------

// V200 home requires *UpdateYear whenever *Update is non-"NOT UPDATED"
// ("PARTIAL UPDATE" / "COMPLETE UPDATE"). Safe-snap to "NOT UPDATED" if the
// caller pairs a non-default status with a missing year, so the invariant is
// impossible to violate from the call site.
//
// IMPORTANT: V200 enum casing for these fields is UPPERCASE, even though the
// EZLynx web UI displays title-case ("Complete Update"). The XML importer is
// stricter than (and inverted from) the form. Verified empirically: title-case
// values fail the V200 home import; uppercase values succeed.
function emitHomeUpdate(prefix, status, year) {
  const safeStatus = status === 'NOT UPDATED' || year ? status : 'NOT UPDATED';
  return [el(`${prefix}Update`, safeStatus), elOmitIfEmpty(`${prefix}UpdateYear`, year)];
}

function buildHomeRatingInfo(home, mortgageDetails, state) {
  const mortgageCount = Array.isArray(mortgageDetails) ? mortgageDetails.length : 0;
  const yearBuilt = home?.yearBuilt;
  // Branch's `swimmingPool` is a *type code* (A/B/N), not Yes/No. 'N' means none.
  const hasPool = home?.swimmingPool && home.swimmingPool !== 'N';
  return wrap('RatingInfo', [
    el('PropertyInsCancelledLapsed', 'No'),
    elOmitIfEmpty('YearBuilt', yearBuilt),
    elOmitIfEmpty('Dwelling', translateV200(BRANCH_TO_V200_DWELLING, home?.typeOfHome)),
    elOmitIfEmpty('NumberOfOccupants', home?.numOccupants),
    elOmitIfEmpty('DwellingUse', translateV200(BRANCH_TO_V200_DWELLING_USE, home?.ownershipStatus)),
    home?.ownershipStatus ? el('DwellingOccupancy', 'Owner Occupied') : '',
    el('DistanceToFireHydrant', formatHomeDistanceToFireHydrant(home?.fireHydrantWithin1000ft)),
    el('WithinCityLimits', 'No'),
    el('DistanceToFireStation', '1'),
    el('WithinFireDistrict', 'No'),
    elOmitIfEmpty('ProtectionClass', home?.fpc),
    elOmitIfEmpty('NumberOfStories', home?.numStories != null ? Math.round(home.numStories) : ''),
    elOmitIfEmpty('NumberOfFullBaths', home?.numFullBathrooms),
    elOmitIfEmpty('Construction', translateV200(BRANCH_TO_V200_CONSTRUCTION, home?.constructionType)),
    // ExteriorWalls is positioned immediately after Construction (semantically
    // adjacent — both describe wall material). Emitted only when Branch has a
    // mappable exteriorWallType; absent in working-reference XMLs, so policies
    // without the field continue to produce the previously-tested output.
    elOmitIfEmpty('ExteriorWalls', translateV200(BRANCH_TO_V200_EXTERIOR_WALLS, home?.exteriorWallType)),
    // V200 home Structure: always "Dwelling". Earlier passthrough of Branch's
    // home.constructionStyle leaked single-letter codes ("R") into the XML
    // when data pulls returned them, and those codes are not in V200's
    // Structure enum. Hardcoding to the verified "Dwelling" value matches
    // every working reference XML we have.
    el('Structure', 'Dwelling'),
    elOmitIfEmpty('Roof', translateV200Roof(home?.roofType, state)),
    el('SwimmingPool', hasPool ? 'Yes' : 'No'),
    el('SwimmingPoolFenced', 'No'),
    el('DogOnPremises', 'No'),
    // Reference always populates HeatingType; we don't track it, so default to a safe enum value.
    el('HeatingType', 'Gas'),
    // V200 *Update XML enum (UPPERCASE — not the title-case the EZLynx UI
    // shows): "NOT UPDATED" | "PARTIAL UPDATE" | "COMPLETE UPDATE".
    // V200 invariant: when *Update is non-"NOT UPDATED", the matching
    // *UpdateYear is REQUIRED. emitHomeUpdate enforces that — safe-snapping
    // to "NOT UPDATED" if a non-default status would be paired with a missing
    // year, rather than emitting "COMPLETE UPDATE" with no year and failing
    // the V200 schema check.
    ...emitHomeUpdate(
      'Roofing',
      home?.roofYear && yearBuilt && home.roofYear > yearBuilt ? 'COMPLETE UPDATE' : 'NOT UPDATED',
      home?.roofYear || yearBuilt
    ),
    ...emitHomeUpdate('Electrical', 'NOT UPDATED', yearBuilt),
    ...emitHomeUpdate('Plumbing', 'NOT UPDATED', yearBuilt),
    ...emitHomeUpdate('Heating', 'NOT UPDATED', yearBuilt),
    el('UnderConstruction', home?.areasUnderConstruction ? 'Yes' : 'No'),
    elOmitIfEmpty('SquareFootage', home?.sqFt),
    elOmitIfEmpty('PurchaseDate', formatDate(home?.purchaseDate)),
    el('Trampoline', home?.hasTrampoline ? 'Yes' : 'No'),
    el('BusinessOnPremises', 'No'),
    el('FirstMortgagee', mortgageCount >= 1 ? 'Yes' : 'No'),
    el('SecondMortgagee', mortgageCount >= 2 ? 'Yes' : 'No'),
    el('ThirdMortgagee', mortgageCount >= 3 ? 'Yes' : 'No'),
    el('EquityLineOfCredit', 'No'),
    el('CoSigner', 'No'),
    el('NumberOfOtherInterests', '0'),
    elOmitIfEmpty('Foundation', translateV200(BRANCH_TO_V200_FOUNDATION, home?.basementType)),
    elOmitIfEmpty('RoofDesign', translateV200(BRANCH_TO_V200_ROOF_DESIGN, home?.roofShape))
  ]);
}

// V200 home <MedicalPayments> enum (verified via EZLynx UI screenshot):
// {1000, 2000, 3000, 4000, 5000}. Branch's policyLimitMedicalPayments lookup
// allows "500" which is NOT in V200's enum and causes import failures.
// Snap Branch's value to the nearest valid V200 value (round up).
function snapHomeMedicalPayments(rawValue) {
  if (rawValue == null || rawValue === '') {
    return '1000';
  }
  const n = parseInt(rawValue, 10);
  if (!Number.isFinite(n) || n <= 1000) {
    return '1000';
  }
  if (n <= 2000) {
    return '2000';
  }
  if (n <= 3000) {
    return '3000';
  }
  if (n <= 4000) {
    return '4000';
  }
  return '5000';
}

// V200 home <DistanceToFireHydrant> is a closed enum. Authoritative buckets
// from the EZLynx UI dropdown (confirmed 2026-05-26):
//   1-500, 501-600, 601-1000,
//   1001-1100, 1101-1200, 1201-1300, 1301-1400, 1401-1500,
//   1501 and Greater, None in Area
// Branch only stores `fireHydrantWithin1000ft` (Boolean) without exact
// distance, so we pick safe defaults at each end. Earlier code emitted
// "1000+" — not in the enum — and got KAILIE_LYONS' home XML rejected
// (see bd branch-qrr). Within-1000ft maps to the mid-bucket "501-600"
// (verified working across multiple reference files); >1000ft maps to
// "1501 and Greater" (title case verified by hand-edit upload).
export function formatHomeDistanceToFireHydrant(fireHydrantWithin1000ft) {
  if (fireHydrantWithin1000ft === false) {
    return '1501 and Greater';
  }
  return '501-600';
}

// Branch overloads `homeCoverage.deductibleAllOther`: values < 1 are fractions
// (e.g. 0.01 = 1% of Coverage A), values >= 1 are flat dollar amounts. Same
// `< 1` threshold the offer/policy validators already use. V200 home's
// <Deductible> accepts the percent string verbatim ("1%"), so we don't need to
// resolve it to dollars — passing the percent through preserves the carrier's
// own interpretation. Previously this rounded the fraction to 0 and EZLynx
// rejected the import (see bd branch-52p / JEANIE_MODICA failing home XML).
export function formatHomeDeductible(value) {
  if (value == null || value <= 0) {
    return '';
  }
  if (value < 1) {
    const pct = Math.round(value * 100);
    return pct > 0 ? `${pct}%` : '';
  }
  return String(Math.round(value));
}

function buildHomeReplacementCost({ home, homeCoverage, condoCoverage, rentersCoverage, policyType, optionType }) {
  let total = '';
  let dwelling = '';
  let lossOfUse = '';
  let personalLiability = '';
  let medicalPayments = '';
  let deductible = '';
  if (policyType === 'HO4') {
    const cov = rentersCoverage || {};
    personalLiability = cov.coverageX != null ? String(cov.coverageX) : '';
    medicalPayments = snapHomeMedicalPayments(cov.coverageY);
    deductible = cov.deductible != null ? String(cov.deductible) : '';
  } else {
    const cov = policyType === 'HO6' ? condoCoverage || {} : homeCoverage || {};
    const replacementTotal = home?.replacementCostEstimate?.total;
    total = replacementTotal != null ? String(Math.round(replacementTotal)) : '';
    if (cov.coverageA != null) {
      dwelling = String(Math.round(cov.coverageA));
    }
    if (cov.coverageA != null && cov.coverageDPctOfA) {
      lossOfUse = String(Math.round(cov.coverageA * (cov.coverageDPctOfA / 100)));
    }
    personalLiability = cov.coverageX != null ? String(cov.coverageX) : '';
    medicalPayments = snapHomeMedicalPayments(cov.coverageF != null ? cov.coverageF : cov.coverageY);
    deductible = formatHomeDeductible(cov.deductibleAllOther);
  }
  return wrap('ReplacementCost', [
    elOmitIfEmpty('ReplacementCost', total),
    elOmitIfEmpty('Dwelling', dwelling),
    elOmitIfEmpty('LossOfUse', lossOfUse),
    elOmitIfEmpty('PersonalLiability', personalLiability),
    elOmitIfEmpty('MedicalPayments', medicalPayments),
    deductible ? wrap('DeductibeInfo', [el('Deductible', deductible)]) : '<DeductibeInfo />',
    wrap('RatingCredits', [
      el('RetireesCredit', 'No'),
      el('MatureDiscount', 'No'),
      el('RetirementCommunity', 'No'),
      el('LimitedAccessCommunity', 'No'),
      el('GatedCommunity', 'No'),
      el('Multipolicy', isBundle(optionType) ? 'Yes' : 'No'),
      el('NonSmoker', 'No')
    ])
  ]);
}

// The working OH/PA reference (Kurt_Muldrew-clean_Home.xml) does NOT include a
// <StateSpecificEndorsements> block; the V200 home schema imports succeed
// without it. We omit entirely to stay schema-clean across states.
function buildHomeEndorsements() {
  return wrap('Endorsements', [
    el('SpecialPersonalProperty', 'No'),
    wrap('ProtectiveDevices', [
      wrap('BurglarAlarm', [el('DeadBolt', 'No'), el('VisibleToNeighbor', 'No'), el('MannedSecurity', 'No')])
    ]),
    wrap('Sinkhole', [el('SinkholeCollapse', 'No')])
  ]);
}

function buildMortgageeValuePairs(mortgageDetails) {
  if (!Array.isArray(mortgageDetails) || mortgageDetails.length === 0) {
    return [];
  }
  const pairs = [];
  mortgageDetails.forEach((m, i) => {
    const rank = i + 1;
    const mAddr = m.mortgageHolderAddress || {};
    pairs.push(
      valuePair(`mortgagee.${rank}.name`, m.mortgageHolderName),
      valuePair(`mortgagee.${rank}.loanNumber`, m.loanNumber),
      valuePair(`mortgagee.${rank}.address`, mAddr.address),
      valuePair(`mortgagee.${rank}.city`, mAddr.city),
      valuePair(`mortgagee.${rank}.state`, mAddr.state),
      valuePair(`mortgagee.${rank}.zip`, mAddr.zip)
    );
  });
  return pairs;
}

// biome-ignore lint/correctness/noUnusedVariables: kept for re-enable once a working ExtendedInfo name is found
function buildHomeExtendedInfo({ home, homeCoverage, rentersCoverage, condoCoverage, policyType }) {
  const pairs = [];
  if (policyType === 'HO4') {
    const cov = rentersCoverage || {};
    pairs.push(
      valuePair('renters.coverageCD', cov.coverageCD),
      valuePair('renters.coverageX', cov.coverageX),
      valuePair('renters.coverageY', cov.coverageY),
      valuePair('renters.deductible', cov.deductible)
    );
  } else {
    const cov = policyType === 'HO6' ? condoCoverage || {} : homeCoverage || {};
    pairs.push(
      valuePair('home.coverageA', cov.coverageA),
      valuePair('home.coverageBPctOfA', cov.coverageBPctOfA),
      valuePair('home.coverageCPctOfA', cov.coverageCPctOfA),
      valuePair('home.coverageDPctOfA', cov.coverageDPctOfA),
      valuePair('home.coverageX', cov.coverageX),
      valuePair('home.coverageY', cov.coverageY),
      valuePair('home.coverageWaterBackup', cov.coverageWaterBackup),
      valuePair('home.deductibleAllOther', cov.deductibleAllOther),
      valuePair('home.deductibleWindHail', cov.deductibleWindHail),
      valuePair('home.deductibleHurricane', cov.deductibleHurricane),
      valuePair('home.deductibleWaterBackup', cov.deductibleWaterBackup),
      cov.windHailExclusion != null ? valuePair('home.windHailExclusion', cov.windHailExclusion ? 'Yes' : 'No') : ''
    );
  }
  pairs.push(...buildMortgageeValuePairs(home?.mortgageDetails));
  return wrap('ExtendedInfo', pairs.filter(Boolean), 'name="BRANCH_DATA"');
}

export function buildHomeXml({ offer, selectedOption }) {
  const quote = offer?.quote;
  if (!quote) {
    return '';
  }

  const option = selectedOption || {};
  const optionType = quote.selectedOption || option.type;
  const policyType = deriveHomePolicyType(optionType);
  const home = quote.home;
  const addr = home?.homeLocation || quote.correctedAddress || {};
  const state = addr.state || quote.correctedAddress?.state || '';

  const drivers = quote.drivers || [];
  const primaryDriver = drivers.find((d) => d.isPrimary) || drivers[0];
  const spouseDriver = findSpouseDriver(drivers);

  const applicantPerson = {
    firstName: quote.fname,
    lastName: quote.lname,
    middleName: '',
    dateOfBirth: primaryDriver?.dateOfBirth,
    gender: primaryDriver?.gender,
    maritalStatus: primaryDriver?.maritalStatus
  };

  const applicantAddr = {
    ...quote.correctedAddress,
    county: home?.county || quote.correctedAddress?.county
  };

  const applicants = [
    buildApplicantBlock({
      applicantType: 'Applicant',
      person: applicantPerson,
      addr: applicantAddr,
      phone: quote.phone,
      format: 'home'
    })
  ];
  if (spouseDriver) {
    applicants.push(
      buildApplicantBlock({
        applicantType: 'CoApplicant',
        person: spouseDriver,
        addr: applicantAddr,
        phone: quote.phone,
        format: 'home'
      })
    );
  }

  const ezHome = wrap(
    'EZHOME',
    [
      ...applicants,
      wrap('PriorPolicyInfo', [
        wrap('YearsWithPriorCarrier', [el('Years', '0'), el('Months', '0')]),
        wrap('YearsWithContinuousCoverage', [el('Years', '0'), el('Months', '0')])
      ]),
      wrap('PolicyInfo', [
        el('PolicyTerm', '12 Month'),
        el('PolicyType', policyType),
        el('Package', isBundle(optionType) ? 'Yes' : 'No'),
        elOmitIfEmpty('Effective', formatDate(quote.global?.homeEffectiveDate)),
        el('CreditCheckAuth', 'Yes')
      ]),
      buildHomeRatingInfo(home, home?.mortgageDetails, state),
      buildHomeReplacementCost({
        home,
        homeCoverage: quote.homeCoverage,
        condoCoverage: quote.condoCoverage,
        rentersCoverage: quote.rentersCoverage,
        policyType,
        optionType
      }),
      buildHomeEndorsements(),
      wrap('GeneralInfo', [el('RatingStateCode', state)])
      // Diagnostic step: BRANCH_DATA ExtendedInfo block omitted because it is
      // not in the EZLynx reference XML files. Re-enable after import works.
      // buildHomeExtendedInfo({
      //   home,
      //   homeCoverage: quote.homeCoverage,
      //   rentersCoverage: quote.rentersCoverage,
      //   condoCoverage: quote.condoCoverage,
      //   policyType
      // })
    ],
    'xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:xsd="http://www.w3.org/2001/XMLSchema" xmlns="http://www.ezlynx.com/XMLSchema/Home/V200"'
  );

  return `<?xml version="1.0" encoding="utf-8"?>\n${ezHome}\n`;
}
